import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { defineMessages, useIntl } from "react-intl";
import styled from "styled-components";
import {
  FindOrganizationsRequest,
  FindOrganizationsResponse,
  Organization,
} from "../../gen/witbooking/loyalty/organization/v1/organization_api_pb";
import { buildOrganizationTree, flattenOrganizationTree } from "./domain/buildOrganizationTree";

const messages = defineMessages({
  view: { id: "app.entityList.tooltip.view", defaultMessage: "View" },
  empty: { id: "loyaltyOrganization.tree.empty", defaultMessage: "No organizations yet." },
  loadError: {
    id: "loyaltyOrganization.tree.loadError",
    defaultMessage: "Could not load the organization tree.",
  },
  partialLoad: {
    id: "loyaltyOrganization.tree.partialLoad",
    defaultMessage: "Some organizations could not be loaded: the tree below is incomplete.",
  },
  agency: { id: "loyaltyOrganization.agency", defaultMessage: "agency" },
  company: { id: "loyaltyOrganization.company", defaultMessage: "company" },
});

const Row = styled.div<{ depth: number }>`
  display: flex;
  align-items: center;
  padding: 0.4rem 0.75rem 0.4rem ${p => 0.75 + p.depth * 1.75}rem;
  border-bottom: 1px solid #f0f1f3;
  background: #fff;
`;

interface Props {
  brandId: string;
  findOrganizations: (request: FindOrganizationsRequest) => Promise<FindOrganizationsResponse>;
  onView: (id: string) => void;
}

const PAGE_SIZE = 200;

// The paged list cannot build a tree across pages, so this loads every page itself.
export function OrganizationHierarchyTree({ brandId, findOrganizations, onView }: Props) {
  const intl = useIntl();
  const [organizations, setOrganizations] = useState<Organization.AsObject[]>();
  const [loadError, setLoadError] = useState(false);
  const [partial, setPartial] = useState(false);

  useEffect(() => {
    // A brand switch or unmount mid-load must not let the old pages land in the state.
    let cancelled = false;
    const load = async () => {
      const all: Organization.AsObject[] = [];
      let pageNumber = 0;
      let totalPages = 1;
      try {
        do {
          const request = new FindOrganizationsRequest();
          request.setBrandId(brandId);
          request.setPageSize(PAGE_SIZE);
          request.setPageNumber(pageNumber);
          const response = await findOrganizations(request);
          all.push(...response.getOrganizationsList().map(o => o.toObject()));
          totalPages = response.getTotalPages() || 1;
          pageNumber += 1;
        } while (!cancelled && pageNumber < totalPages);
      } catch (e) {
        if (cancelled) return;
        // Whatever loaded is still worth drawing, with a warning saying it is not everything.
        console.error(`Loading the organization tree failed on page ${pageNumber}`, e);
        if (all.length === 0) {
          setLoadError(true);
          return;
        }
        setPartial(true);
      }
      if (!cancelled) setOrganizations(all);
    };
    if (brandId) load();
    return () => {
      cancelled = true;
    };
  }, [brandId, findOrganizations]);

  const rows = useMemo(() => flattenOrganizationTree(buildOrganizationTree(organizations ?? [])), [
    organizations,
  ]);

  return (
    <div>
      {loadError && <Alert variant="danger">{intl.formatMessage(messages.loadError)}</Alert>}
      {partial && <Alert variant="warning">{intl.formatMessage(messages.partialLoad)}</Alert>}
      {organizations && rows.length === 0 && (
        <div className="text-muted p-3 text-center">{intl.formatMessage(messages.empty)}</div>
      )}
      {rows.map(({ node, depth }) => (
        <Row key={node.id} depth={depth}>
          <Badge variant={node.agency ? "info" : "secondary"} className="text-uppercase mr-2">
            {intl.formatMessage(node.agency ? messages.agency : messages.company)}
          </Badge>
          <span className="flex-grow-1">
            {node.name} <small className="text-muted">{node.ticker}</small>
          </span>
          <Button
            size="sm"
            variant="light"
            title={intl.formatMessage(messages.view)}
            onClick={() => onView(node.id)}>
            <FontAwesomeIcon icon={faEye} />
          </Button>
        </Row>
      ))}
    </div>
  );
}
